import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Divider,
  ToggleButton, ToggleButtonGroup, Alert,
} from "@mui/material";
import api from "../services/api";

const ACTIVE_MINUTES = 15; // a device counts as "active" if seen within this

/* Parse a User-Agent into a friendly device label. */
function parseUA(ua) {
  ua = ua || "";
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(ua);
  let os = "Unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/Mac OS X|Macintosh/i.test(ua)) os = "Mac";
  else if (/CrOS/i.test(ua)) os = "ChromeOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  let browser = "Browser";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\//i.test(ua)) browser = "Opera";
  else if (/Chrome\//i.test(ua)) browser = "Chrome";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
  return { isMobile, icon: isMobile ? "📱" : "💻", os, browser };
}

const ago = (d) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const isActive = (lastSeen) => (Date.now() - new Date(lastSeen).getTime()) <= ACTIVE_MINUTES * 60000;

export default function ActiveSessions() {
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState("all"); // all | active | inactive
  const [sessions, setSessions] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get("/companies/activeSessions", { params: { days } })
      .then((r) => { setSessions(Array.isArray(r.data?.sessions) ? r.data.sessions : []); setLoadedAt(new Date()); setError(null); })
      .catch((err) => {
        // Surface the real failure instead of silently showing "no devices":
        // a stale cache, a 403/404 (backend not redeployed), or a network error
        // all look identical otherwise.
        const status = err.response?.status;
        const detail = err.response?.data?.error || err.response?.data?.message || err.message;
        setError(status ? `Request failed (HTTP ${status}): ${detail}` : `Could not reach the server: ${detail}`);
        setSessions([]);
      });
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const withActive = sessions.map((s) => ({ ...s, active: isActive(s.LastSeen) }));
  const totalActive = withActive.filter((s) => s.active).length;
  const totalInactive = withActive.length - totalActive;
  const shown = withActive.filter((s) => filter === "all" || (filter === "active" ? s.active : !s.active));

  const byCompany = new Map();
  for (const s of shown) {
    const key = s.CompanyName || `Company ${s.Company_ID}`;
    if (!byCompany.has(key)) byCompany.set(key, []);
    byCompany.get(key).push(s);
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Devices using the app</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Seen in the last {days} day{days === 1 ? "" : "s"} · "active" = used within {ACTIVE_MINUTES} min
            {loadedAt ? ` · updated ${ago(loadedAt)}` : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField select size="small" label="Lookback" value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 120 }}>
            <MenuItem value={1}>1 day</MenuItem>
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={365}>1 year</MenuItem>
          </TextField>
          <Button size="small" variant="outlined" onClick={load}>Refresh</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${withActive.length} total device${withActive.length === 1 ? "" : "s"}`} />
        <Chip size="small" color="success" label={`${totalActive} active now`} />
        <Chip size="small" variant="outlined" label={`${totalInactive} inactive`} />
        <Chip size="small" variant="outlined" label={`${byCompany.size} compan${byCompany.size === 1 ? "y" : "ies"}`} />
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ ml: "auto" }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {[...byCompany.entries()].map(([company, list]) => {
        const act = list.filter((s) => s.active).length;
        return (
          <Box key={company} sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600 }}>
              {company} — {list.length} device{list.length === 1 ? "" : "s"} ({act} active)
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell><TableCell>Device</TableCell><TableCell>User</TableCell>
                    <TableCell>IP</TableCell><TableCell align="right">Last active</TableCell><TableCell align="right">First seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((s, i) => {
                    const ua = parseUA(s.UserAgent);
                    return (
                      <TableRow key={i}>
                        <TableCell><Chip size="small" color={s.active ? "success" : "default"} variant={s.active ? "filled" : "outlined"} label={s.active ? "Active" : "Inactive"} /></TableCell>
                        <TableCell>{ua.icon} {ua.os} · {ua.browser}</TableCell>
                        <TableCell>{s.Username}</TableCell>
                        <TableCell>{s.IPAddress}</TableCell>
                        <TableCell align="right">{ago(s.LastSeen)}</TableCell>
                        <TableCell align="right">{ago(s.FirstSeen)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Divider sx={{ mt: 1 }} />
          </Box>
        );
      })}
      {!shown.length && !error && (
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>No devices in this view.</Typography>
      )}
    </Paper>
  );
}
